import React from 'react';
import Check from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import translate from '@/lib/intl/translate';
import { isValidPassword } from '@/utils/passwordValidator';

const originalPasswordFieldValue = 'originalPassword';
const newPasswordFieldValue = 'password';

function EditEmployeeChangePassword({ register, errors, trigger, watch }) {
  const [originalPassword, setOriginalPassword] = React.useState({
    showPassword: false,
  });
  const [password, setPassword] = React.useState({
    showPassword: false,
    hasCharacterCount: null,
    hasNumber: null,
    hasUpper: null,
    hasLower: null,
    hasSpecial: null,
  });

  const handleClickShowOriginalPassword = () => {
    setOriginalPassword((prevOriginalPassword) => ({
      showPassword: !prevOriginalPassword.showPassword,
    }));
  };

  const watchOriginalPassword = watch(originalPasswordFieldValue);
  const watchNewPassword = watch(newPasswordFieldValue);

  React.useEffect(() => {
    if (!watchNewPassword) {
      setPassword({
        ...password,
        hasCharacterCount: null,
        hasNumber: null,
        hasUpper: null,
        hasLower: null,
        hasSpecial: null,
      });
    } else {
      const { hasCharacterCount, hasNumber, hasUpper, hasLower, hasSpecial } =
        isValidPassword(watchNewPassword);

      setPassword({
        ...password,
        hasCharacterCount,
        hasNumber,
        hasUpper,
        hasLower,
        hasSpecial,
      });
    }
  }, [watchNewPassword]);

  const handleClickShowPassword = () => {
    setPassword((prevPassword) => ({
      ...prevPassword,
      showPassword: !prevPassword.showPassword,
    }));
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const renderPasswordValidationCase = (text, isValid) => {
    const iconStyles = { fontSize: '1rem', paddingRight: '4px' };

    // Pristine state
    let icon = <ClearIcon color="disabled" style={iconStyles} />;

    if (isValid) {
      icon = (
        <Check
          data-testid="password-condition-success"
          color="primary"
          style={iconStyles}
        />
      );
    } else if (isValid === false) {
      icon = (
        <ClearIcon
          data-testid="password-condition-error"
          style={{ ...iconStyles, color: 'red' }}
        />
      );
    }

    return (
      <li className="flex">
        {icon}
        <div className="text-[12px] type-caption1 sm:text-[10px]">{text}</div>
      </li>
    );
  };

  const { ref: originalPasswordRef, ...originalPasswordRest } = register(
    originalPasswordFieldValue,
    {
      validate: () => {
        const isNewPasswordProvided = Boolean(watchNewPassword);
        const isOriginalPasswordProvided = Boolean(watchOriginalPassword);

        // If new password is provided require the original password
        if (isNewPasswordProvided) {
          if (!isOriginalPasswordProvided) {
            return 'Original password required';
          }

          return isOriginalPasswordProvided;
        }

        // If new password not provided, this field is not required
        return true;
      },
    },
  );
  const { ref: newPasswordRef, ...newPasswordRest } = register(
    newPasswordFieldValue,
    {
      validate: (value) => {
        // New password is not required
        if (!value) {
          return true;
        }

        const { isValid } = isValidPassword(value);

        return isValid;
      },
    },
  );

  return (
    <div className="m-0 grid gap-4 border-t border-grey-300 p-6 sm:grid-cols-2">
      <div className="type-subtitle1 col-span-full">
        {translate('EMPLOYEE_CHANGE_PASSWORD')}
      </div>
      <div>
        <TextField
          variant="outlined"
          id="outlined-adornment-original-password"
          fullWidth
          label={translate('EMPLOYEE_ORIGINAL_PASSWORD')}
          type={originalPassword.showPassword ? 'text' : 'password'}
          error={!!errors.originalPassword}
          onBlur={trigger}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowOriginalPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  size="large"
                >
                  {originalPassword.showPassword ? (
                    <Visibility />
                  ) : (
                    <VisibilityOff />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          inputRef={originalPasswordRef}
          {...originalPasswordRest}
        />
        {/* Only show text for password required, custom icon error state shows the rest */}
        {errors && errors.originalPassword ? (
          <p
            data-testid="password-form-error-state"
            className="m-1 text-xs text-error-main"
          >
            {errors.originalPassword.message}
          </p>
        ) : null}
      </div>
      <div>
        <FormControl fullWidth error={!!errors.password} variant="outlined">
          <InputLabel htmlFor="outlined-adornment-password">
            {translate('EMPLOYEE_NEW_PASSWORD')}
          </InputLabel>
          <OutlinedInput
            id="outlined-adornment-password"
            fullWidth
            type={password.showPassword ? 'text' : 'password'}
            onBlur={trigger}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  size="large"
                >
                  {password.showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
            label={translate('EMPLOYEE_NEW_PASSWORD')}
            inputRef={newPasswordRef}
            {...newPasswordRest}
          />
          {/* Only show text for password required, custom icon error state shows the rest */}
          {errors && errors.password && errors.password.type === 'required' ? (
            <p
              data-testid="password-form-error-state"
              className="m-1 text-xs text-error-main"
            >
              {errors.password.message}
            </p>
          ) : null}
        </FormControl>
        <ul className="grid grid-cols-2 pt-1.5">
          {renderPasswordValidationCase(
            translate('PASSWORD_CHARACTER_LIMIT'),
            password.hasCharacterCount,
          )}
          {renderPasswordValidationCase(
            translate('PASSWORD_CONTAINS_NUMBER'),
            password.hasNumber,
          )}
          {renderPasswordValidationCase(
            translate('PASSWORD_CONTAINS_UPPERCASE'),
            password.hasUpper,
          )}
          {renderPasswordValidationCase(
            translate('PASSWORD_CONTAINS_SYMBOL'),
            password.hasSpecial,
          )}
          {renderPasswordValidationCase(
            translate('PASSWORD_CONTAINS_LOWERCASE'),
            password.hasLower,
          )}
        </ul>
      </div>
    </div>
  );
}

EditEmployeeChangePassword.propTypes = {
  register: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    originalPassword: PropTypes.shape({
      message: PropTypes.string,
      type: PropTypes.string,
    }),
    password: PropTypes.shape({
      message: PropTypes.string,
      type: PropTypes.string,
    }),
  }).isRequired,
  trigger: PropTypes.func.isRequired,
  watch: PropTypes.func.isRequired,
};

export { EditEmployeeChangePassword };
